﻿import { FC } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, CardFooter, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { trackConsultationClick } from '../analytics/events';
import { PageProps } from './HomePage';

export const CareerCoachingPage: FC<PageProps> = ({ onNavigate }) => {
  const handleConsultation = (programName: string) => {
    trackConsultationClick(programName, 'career_coaching_page');
    onNavigate(ROUTES.CONTACT);
  };

  const programs = [
    {
      id: 'starter-clarity',
      name: 'Starter Career Clarity Program',
      duration: '3 Weeks • 6 Intensive Sessions',
      bestFor: 'Students, freshers, and early career job seekers',
      badge: 'Early Career',
      description: 'Establish clear career direction, bridge foundational skill gaps, and learn how to present your background effectively to entry-level recruiters.',
      features: [
        'Comprehensive career aspiration & strengths discovery',
        'Academic-to-professional skill mapping & gap audit',
        'Entry-level ATS resume formatting & baseline review',
        'LinkedIn profile foundation setup & presence guidance',
        'Introductory behavioral interview readiness & STAR framework',
        'Personalized 60-day job search & skill acquisition action plan'
      ],
      ctaText: 'Apply for Starter Program'
    },
    {
      id: 'pro-accelerator',
      name: 'Pro Career Accelerator Program',
      duration: '5 Weeks • 10 In-Depth Sessions',
      bestFor: 'Working professionals, mid-level candidates & career switchers',
      badge: 'Most Popular',
      popular: true,
      description: 'A comprehensive, strategic career upgrade designed to position you for higher-impact roles, career pivots, and competitive compensation packages.',
      features: [
        'Complete ATS resume optimization & structural overhaul',
        'Targeted cover letter architecture & modular variations',
        'End-to-end LinkedIn profile optimization & algorithmic visibility',
        'Target domain keyword mapping & recruiter search targeting',
        'Rigorous company research & competitive landscape teardown',
        'Elevator pitch development & executive presence coaching',
        'Data-backed salary negotiation framework & counter-offer playbook',
        'Proactive direct-outreach & hidden job market search strategy'
      ],
      ctaText: 'Apply for Pro Accelerator'
    },
    {
      id: 'premium-transformation',
      name: 'Premium Career Transformation',
      duration: 'Personalized Advisory • Cohort / Application Based',
      bestFor: 'Senior professionals, managers, and complex cross-industry switchers',
      badge: 'Executive & High-Touch',
      description: 'Bespoke, private 1-on-1 mentorship for senior professionals navigating executive transitions, lateral industry shifts, or complex career plateaus.',
      features: [
        'Custom 1-on-1 advisory roadmap tailored to your specific timeline',
        'Executive branding, thought-leadership positioning & bio crafting',
        'Senior leadership & stakeholder panel mock interview simulations',
        'Confidential executive compensation, equity, & bonus advisory',
        'Continuous direct feedback & iterative messaging review',
        'Long-term career trajectory & leadership growth roadmap'
      ],
      ctaText: 'Book a Consultation'
    }
  ];

  const methodologyPillars = [
    {
      title: 'Objective Market Alignment',
      desc: 'We analyze your target market with realistic hiring data to ensure your positioning matches actual employer requirements.'
    },
    {
      title: 'Iterative Coaching Sessions',
      desc: 'Each session combines strategic analysis with concrete deliverables — you leave every conversation with finalized assets.'
    },
    {
      title: 'Ethical & Realistic Standards',
      desc: 'We focus on genuine capability building and high-leverage positioning. We never promise guaranteed jobs or artificial salary spikes.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Career Coaching Programs & Mentorship"
        description="Structured, 1-on-1 executive and professional career coaching. Master ATS resumes, LinkedIn presence, interview simulations, and salary negotiation."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <Badge variant="primary" style={{ marginBottom: '1rem' }}>Personalized Mentorship</Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Professional Career Coaching Programs
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1.25rem', lineHeight: 1.65 }}>
              Structured, personalized coaching engineered to eliminate ambiguity, master competitive interview processes, and accelerate your career progression.
            </p>
          </div>
        </Container>
      </section>

      {/* Programs Grid */}
      <Section title="Structured Coaching Programs" subtitle="Select the program aligned with your career stage and transition goals.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
          {programs.map(prog => (
            <Card
              key={prog.id}
              className="cs-card-hover"
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '2rem',
                border: prog.popular ? '2px solid var(--color-brand-primary)' : '1px solid var(--color-border-subtle)',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <Badge variant={prog.popular ? 'primary' : 'neutral'}>{prog.badge}</Badge>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                  {prog.duration.split('•')[0]}
                </span>
              </div>

              <CardTitle style={{ fontSize: '1.35rem' }}>{prog.name}</CardTitle>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-brand-primary)', margin: '0.35rem 0 0.85rem 0' }}>
                {prog.duration}
              </div>

              <CardDescription style={{ lineHeight: 1.6, fontSize: '0.925rem', marginBottom: '1.25rem' }}>
                {prog.description}
              </CardDescription>

              <div style={{ padding: '0.75rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)' }}>
                  Best For:
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.2rem' }}>
                  {prog.bestFor}
                </div>
              </div>

              <div style={{ flex: '1 0 auto' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.75rem' }}>
                  Program Inclusions:
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  {prog.features.map((feat, i) => (
                    <li key={i} style={{ display: 'flex', gap: '0.5rem', lineHeight: 1.45 }}>
                      <span style={{ color: 'var(--color-accent-teal)', fontWeight: 700 }}>✓</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <CardFooter style={{ marginTop: '2rem', paddingTop: '1.25rem' }}>
                <Button
                  variant={prog.popular ? 'primary' : 'outline'}
                  size="md"
                  onClick={() => handleConsultation(prog.name)}
                  style={{ width: '100%' }}
                >
                  {prog.ctaText}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </Section>

      {/* Coaching Methodology */}
      <Section
        title="Our Coaching Philosophy"
        subtitle="Transparent, grounded guidance focused on real skills and measurable positioning."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="cs-grid-3">
          {methodologyPillars.map((p, idx) => (
            <Card key={idx} style={{ padding: '1.75rem' }}>
              <CardTitle style={{ fontSize: '1.15rem' }}>{p.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.6rem', lineHeight: 1.6, fontSize: '0.925rem' }}>
                {p.desc}
              </CardDescription>
            </Card>
          ))}
        </div>

        <div style={{ marginTop: '2.5rem', padding: '1.5rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)', maxWidth: '800px', margin: '2.5rem auto 0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-navy-dark)' }}>
            Ethical Commitment Notice
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.35rem', lineHeight: 1.6 }}>
            Connect Source does not sell artificial interview guarantees, proxy support, or guaranteed salary placements. We provide professional coaching, strategic positioning, and rigorous preparation so you perform at your best.
          </p>
        </div>
      </Section>

      {/* Consultation Anchor CTA */}
      <section style={{ padding: '4.5rem 0', backgroundColor: 'var(--color-bg-canvas)' }}>
        <Container size="md">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
              Unsure Which Program Fits Your Goals?
            </h2>
            <p style={{ fontSize: '1.05rem', color: 'var(--color-text-secondary)', marginTop: '0.85rem', lineHeight: 1.6 }}>
              Schedule an introductory conversation with Coach Nilesh Mishra. We'll evaluate your current career situation and identify the right path forward.
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleConsultation('coaching_page_bottom_cta')}
              >
                Schedule an Introductory Discussion
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};


